import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useDashboard } from '@/context/DashboardContext';
import { Cloud, Repeat, PanelLeft } from 'lucide-react';
import { getNavigationItems, siteDetailNavigationItems } from '@/lib/navigation';
import { colorPresetService } from '@/lib/colorPresetService';
import { boardService } from '@/lib/boardService';
import { useTeamChat } from '@/context/TeamChatContext';
import { supabase } from '@/lib/supabase';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { SearchOverlay } from './SearchOverlay';
import { WorkspaceSwitcherPopup } from './WorkspaceSwitcherPopup';

interface SidebarProps {
  isGlobalDarkMode: boolean;
  setIsGlobalDarkMode?: (dark: boolean) => void;
  isCollapsed: boolean;
  setIsCollapsed?: (collapsed: boolean) => void;
  onToggleSidebar?: () => void;
  activeSubmenu: string | null;
  setActiveSubmenu: (submenu: string | null) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  isGlobalDarkMode,
  isCollapsed,
  setIsCollapsed,
  onToggleSidebar,
  activeSubmenu,
  setActiveSubmenu
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentWorkspace, userRole } = useWorkspace();
  const { currentDashboard, switchDashboard } = useDashboard();
  const { getTotalUnreadCount } = useTeamChat();
  const [showSearchOverlay, setShowSearchOverlay] = useState(false);

  const handleToggleSidebar = () => {
    if (onToggleSidebar) {
      onToggleSidebar();
    } else if (setIsCollapsed) {
      setIsCollapsed(!isCollapsed);
      window.dispatchEvent(new CustomEvent('toggle-secondary-sidebar', { detail: { collapsed: !isCollapsed } }));
    }
  };

  // Detect if we're on a site detail page  
  const siteDetailMatch = location.pathname.match(/^\/sites\/([^/]+)\//);
  const isOnSiteDetail = !!siteDetailMatch;
  const currentSiteId = siteDetailMatch?.[1] ?? null;

  // Backup Storage States
  const [backupStorage, setBackupStorage] = useState<{ used: number; total: number } | null>(null);
  const [loadingStorage, setLoadingStorage] = useState(false);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  useEffect(() => {
    if ((activeSubmenu === 'sites' || isOnSiteDetail) && currentWorkspace?.id) {
      setLoadingStorage(true);
      supabase.functions.invoke('r2-backups', {
        body: { action: 'WORKSPACE_STORAGE', workspaceId: currentWorkspace.id }
      }).then(({ data, error }) => {
        if (!error && data) {
          setBackupStorage(data);
        }
      }).finally(() => {
        setLoadingStorage(false);
      });
    }
  }, [activeSubmenu, isOnSiteDetail, currentWorkspace?.id]);

  // Keyboard shortcut and global event for search
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setShowSearchOverlay((open) => !open);
      }
    };
    
    const handleOpenSearch = () => {
      setShowSearchOverlay(true);
    };

    document.addEventListener("keydown", down);
    window.addEventListener("open-global-search", handleOpenSearch as EventListener);
    
    return () => {
      document.removeEventListener("keydown", down);
      window.removeEventListener("open-global-search", handleOpenSearch as EventListener);
    };
  }, []);



  const navigationItems = React.useMemo(() => {
    const baseHiddenIds = currentWorkspace?.appearance?.menu?.hiddenIds || [];
    const roleHiddenIds = currentWorkspace?.appearance?.menu?.roleHiddenIds || {};
    
    // Determine the actual hidden IDs for the current user
    let hiddenIds = baseHiddenIds;
    
    // If user has a role and it's not owner, check for overrides
    if (userRole && userRole !== 'owner') {
      // If there is an override array for this role, it completely supersedes the base list
      if (Array.isArray(roleHiddenIds[userRole])) {
        hiddenIds = roleHiddenIds[userRole];
      }
    }

    const dashboardItems = getNavigationItems(currentDashboard);

    return dashboardItems
      .map(item => {
        // If the item itself is hidden, return null
        if (hiddenIds.includes(item.id)) return null;

        // If it has subitems, filter them
        if (item.hasSubItems && item.subItems) {
          const visibleSubItems = item.subItems.filter(subItem => !hiddenIds.includes(subItem.id));

          // Return item with filtered subitems
          return {
            ...item,
            subItems: visibleSubItems
          };
        }

        return item;
      })
      .filter((item): item is typeof dashboardItems[0] => item !== null);
  }, [currentWorkspace?.appearance?.menu?.hiddenIds, currentWorkspace?.appearance?.menu?.roleHiddenIds, userRole, currentDashboard]);


  const isActivePath = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isBrandActive = () => {
    return location.pathname.startsWith('/brand') && location.pathname !== '/files';
  };

  const isCrmActive = () => {
    return (location.pathname.startsWith('/crm') && location.pathname !== '/crm/home')
      || location.pathname === '/deals' || location.pathname.startsWith('/deals/')
      || location.pathname === '/proposals' || location.pathname.startsWith('/proposals/')
      || location.pathname === '/invoices' || location.pathname.startsWith('/invoices/');
  };

  const isServicesActive = () => {
    return location.pathname.startsWith('/services') && !location.pathname.startsWith('/services/invoices');
  };

  const isBoardsActive = () => {
    return (location.pathname.startsWith('/task-board') || location.pathname.startsWith('/boards'))
      && !location.pathname.startsWith('/boards/tickets')
      && !location.pathname.startsWith('/boards/forms')
      && !location.pathname.startsWith('/boards/proposals');
  };

  const isInboxActive = () => {
    return location.pathname.startsWith('/inbox');
  };

  const getParentMenuFromPath = (path: string): string | null => {
    if (path === '/files') {
      return null;
    }
    const item = navigationItems.find(navItem => {
      if (navItem.hasSubItems && navItem.subItems) {
        return navItem.subItems.some(subItem => path === subItem.path || path.startsWith(subItem.path + '/'));
      }
      return false;
    });
    return item ? item.id : null;
  };

  const handleMenuClick = (itemId: string, hasSubItems: boolean, path: string) => {
    if (hasSubItems) {
      const menuItem = navigationItems.find(item => item.id === itemId);
      if (activeSubmenu !== itemId) {
        setActiveSubmenu(itemId);
        if (menuItem?.subItems && menuItem.subItems.length > 0) {
          navigate(menuItem.subItems[0].path);
        }
      }
    } else {
      setActiveSubmenu(null);
      navigate(path);
    }
  };

  const currentActiveSubmenu = navigationItems.find(item => item.id === activeSubmenu);

  // When entering/leaving site detail, keep sites submenu open
  useEffect(() => {
    if (isOnSiteDetail) {
      setActiveSubmenu('sites');
      return;
    }
    const parentMenu = getParentMenuFromPath(location.pathname);
    if (parentMenu && !activeSubmenu) {
      setActiveSubmenu(parentMenu);
    } else if (!parentMenu) {
      setActiveSubmenu(null);
    }
  }, [location.pathname, isOnSiteDetail]);


  const isFileManagerPage = location.pathname.startsWith('/files');
  const isInboxPage = location.pathname.startsWith('/inbox');
  const hasSecondaryMenu = Boolean(activeSubmenu && (currentActiveSubmenu?.hasSubItems || isOnSiteDetail));
  const isExpanded = (hasSecondaryMenu || isFileManagerPage || isInboxPage) && !isCollapsed;

  return (
    <div className="sidebar-container flex">
      <SearchOverlay
        open={showSearchOverlay}
        onOpenChange={setShowSearchOverlay}
        isGlobalDarkMode={isGlobalDarkMode}
      />

      {/* Unified Single-Box Top Workspace Brand Header */}
      <div
        className={`fixed left-0 top-0 h-14 transition-all duration-300 ease-in-out z-40 border-b flex items-center ${
          isExpanded ? 'w-[18rem]' : 'w-16'
        } ${isGlobalDarkMode ? 'bg-sidebar border-sidebar-border' : 'bg-sidebar-light border-gray-200'}`}
      >
        <WorkspaceSwitcherPopup
          isGlobalDarkMode={isGlobalDarkMode}
          isExpanded={isExpanded}
        />
      </div>

      {/* Main Sidebar (Icon Navigation) */}
      <div
        className={`fixed left-0 top-14 h-[calc(100vh-3.5rem)] w-16 border-r transition-all duration-300 z-30 ${isGlobalDarkMode
          ? 'bg-sidebar border-sidebar-border'
          : 'bg-sidebar-light border-gray-200'
          }`}
      >
        {/* Navigation Items */}
        <nav className="py-3">
          <ul className="space-y-2">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isSitesActive = () => location.pathname.startsWith('/sites');
              
              const isActive = item.hasSubItems
                ? (item.id === 'brand' ? isBrandActive() : 
                   item.id === 'crm' ? isCrmActive() : 
                   item.id === 'sites' ? isSitesActive() : 
                   item.id === 'services' ? isServicesActive() : 
                   isBoardsActive())
                : (item.id === 'inbox' ? isInboxActive()
                  : (item.id === 'support-tickets' ? (isActivePath('/ticket') || isActivePath('/tickets') || isActivePath('/crm/tickets') || isActivePath('/boards/tickets'))
                  : (item.id === 'knowledge-base' ? (isActivePath('/knowledge-base') || isActivePath('/crm/knowledge-base') || isActivePath('/boards/knowledge-base'))
                  : (item.id === 'forms' ? (isActivePath('/forms') || isActivePath('/crm/forms') || isActivePath('/boards/forms'))
                  : isActivePath(item.path)))));

              const showUnreadIndicator = item.id === 'inbox' && getTotalUnreadCount() > 0;

              return (
                <React.Fragment key={item.id}>
                  <li className="px-2">
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => handleMenuClick(item.id, item.hasSubItems || false, item.path)}
                          onMouseEnter={() => {
                            if (item.id === 'boards' && currentWorkspace) {
                              boardService.getBoardsByWorkspace(currentWorkspace.id);
                            }
                          }}
                          className={`w-9 h-9 mx-auto flex items-center justify-center rounded-full transition-all duration-200 border ${isActive
                            ? isGlobalDarkMode
                              ? 'bg-nav-active text-white shadow-lg border-transparent'
                              : 'bg-sidebar-light-active text-gray-900 shadow-sm border-gray-200'
                            : isGlobalDarkMode
                              ? 'text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent border-transparent'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-sidebar-light-hover border-transparent'
                            }`}
                        >
                          <div className="relative">
                            <Icon className="w-5 h-5" />
                            {showUnreadIndicator && (
                              <span className={`absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full ring-1 ${isGlobalDarkMode ? 'ring-sidebar' : 'ring-white'}`} />
                            )}
                          </div>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="font-medium">
                        {item.label}
                      </TooltipContent>
                    </Tooltip>
                  </li>

                </React.Fragment>
              );
            })}
          </ul>
        </nav>

        {/* Bottom Actions: Sidebar Toggle + Dashboard Switcher */}
        <div className="absolute bottom-3 left-0 right-0 flex flex-col items-center gap-2 px-2">
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button
                onClick={handleToggleSidebar}
                className={`w-9 h-9 flex items-center justify-center rounded-full transition-all duration-200 border cursor-pointer ${
                  isGlobalDarkMode
                    ? 'text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent border-sidebar-border bg-sidebar-accent/50'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100 border-gray-200 bg-gray-50'
                }`}
                aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                <PanelLeft className="w-4 h-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="font-medium">
              {isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            </TooltipContent>
          </Tooltip>

          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button
                onClick={() => switchDashboard(currentDashboard === 'agency' ? 'crm' : 'agency')}
                className={`w-9 h-9 flex items-center justify-center rounded-full transition-all duration-200 border cursor-pointer ${
                  isGlobalDarkMode
                    ? 'text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent border-sidebar-border bg-sidebar-accent/50'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100 border-gray-200 bg-gray-50'
                }`}
              >
                <Repeat className="w-4 h-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="font-medium">
              {currentDashboard === 'agency' ? 'Switch to CRM' : 'Switch to Agency'}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Secondary Sidebar for Submenus */}
      {
        activeSubmenu && (currentActiveSubmenu?.hasSubItems || isOnSiteDetail) && (
          <div
            className={`fixed left-16 top-14 h-[calc(100vh-3.5rem)] w-56 border-r transition-all duration-300 z-20 flex flex-col ${
              isCollapsed ? '-translate-x-full opacity-0 pointer-events-none' : 'translate-x-0 opacity-100'
            } ${isGlobalDarkMode
              ? 'bg-sidebar border-sidebar-border'
              : 'bg-sidebar-light-secondary border-gray-200'
              }`}
            style={undefined}
          >
            {isOnSiteDetail ? (
              <>
                {/* Site Detail Nav Items */}
                <nav className="p-3 pb-28 flex-1 overflow-y-auto">
                  <ul className="space-y-1">
                    {siteDetailNavigationItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname.endsWith(`/${item.path}`);
                      return (
                        <li key={item.id}>
                          <button
                            onClick={() => navigate(`/sites/${currentSiteId}/${item.path}`)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 border ${
                              isActive
                                ? isGlobalDarkMode
                                  ? 'bg-nav-active text-white shadow-md border-transparent'
                                  : 'bg-sidebar-light-active text-gray-900 shadow-sm border-gray-200'
                                : isGlobalDarkMode
                                  ? 'text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent border-transparent'
                                  : 'text-gray-700 hover:text-gray-900 hover:bg-sidebar-light-hover border-transparent'
                            }`}
                          >
                            <Icon className="w-4 h-4 flex-shrink-0" />
                            <span>{item.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </nav>

                {/* Backup Storage Indicator */}
                {backupStorage && (
                  <div className={`absolute bottom-0 left-0 right-0 p-4 border-t ${isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Cloud size={14} className={isGlobalDarkMode ? 'text-sidebar-foreground/60' : 'text-gray-500'} />
                      <span className={`text-[11px] font-bold uppercase tracking-wider ${isGlobalDarkMode ? 'text-sidebar-foreground/60' : 'text-gray-500'}`}>Backup Storage</span>
                    </div>
                    
                    <div className={`w-full rounded-full h-1.5 mb-2 ${isGlobalDarkMode ? 'bg-sidebar-accent' : 'bg-gray-200'}`}>
                      <div
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          (backupStorage.used / backupStorage.total) > 0.9 ? 'bg-red-500' : 'bg-nav-active'
                        }`}
                        style={{ width: `${Math.min((backupStorage.used / backupStorage.total) * 100, 100)}%` }}
                      />
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <p className={`text-[10px] font-medium ${isGlobalDarkMode ? 'text-sidebar-foreground/60' : 'text-gray-500'}`}>
                        {formatFileSize(backupStorage.used)} of {formatFileSize(backupStorage.total)}
                      </p>
                      {loadingStorage && (
                        <div className="w-2.5 h-2.5 border-2 border-nav-active border-t-transparent rounded-full animate-spin" />
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Submenu Items */}
                <nav className="p-3 pb-28 flex-1 overflow-y-auto">
                  <ul className="space-y-1">
                    {currentActiveSubmenu?.subItems?.map((subItem) => {
                      const SubIcon = subItem.icon;
                      const isSubActive = (() => {
                        const path = subItem.path;
                        if (location.pathname === path) return true;
                        if (location.pathname.startsWith(path + '/')) {
                          return !currentActiveSubmenu?.subItems?.some(item =>
                            item.path !== path &&
                            item.path.startsWith(path) &&
                            location.pathname.startsWith(item.path)
                          );
                        }
                        return false;
                      })();

                      return (
                        <li key={subItem.id}>
                          <button
                            onClick={() => {
                              navigate(subItem.path);
                            }}
                            onMouseEnter={() => {
                              if (subItem.id === 'color-presets' && currentWorkspace) {
                                colorPresetService.prefetchWorkspacePresets(currentWorkspace.id);
                                colorPresetService.prefetchTemplates(); // Also helpful to prewarm templates
                              } else if (subItem.id === 'task-board' && currentWorkspace) {
                                boardService.getBoardsByWorkspace(currentWorkspace.id);
                              }
                            }}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 border ${isSubActive
                              ? isGlobalDarkMode
                                ? 'bg-nav-active text-white shadow-md border-transparent'
                                : 'bg-sidebar-light-active text-gray-900 shadow-sm border-gray-200'
                              : isGlobalDarkMode
                                ? 'text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent border-transparent'
                                : 'text-gray-700 hover:text-gray-900 hover:bg-sidebar-light-hover border-transparent'
                              }`}
                          >
                            <SubIcon className="w-4 h-4 flex-shrink-0" />
                            <span>{subItem.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </nav>

                {/* Backup Storage Indicator */}
                {activeSubmenu === 'sites' && backupStorage && (
                  <div className={`absolute bottom-0 left-0 right-0 p-4 border-t ${isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Cloud size={14} className={isGlobalDarkMode ? 'text-sidebar-foreground/60' : 'text-gray-500'} />
                      <span className={`text-[11px] font-bold uppercase tracking-wider ${isGlobalDarkMode ? 'text-sidebar-foreground/60' : 'text-gray-500'}`}>Backup Storage</span>
                    </div>
                    
                    <div className={`w-full rounded-full h-1.5 mb-2 ${isGlobalDarkMode ? 'bg-sidebar-accent' : 'bg-gray-200'}`}>
                      <div
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          (backupStorage.used / backupStorage.total) > 0.9 ? 'bg-red-500' : 'bg-nav-active'
                        }`}
                        style={{ width: `${Math.min((backupStorage.used / backupStorage.total) * 100, 100)}%` }}
                      />
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <p className={`text-[10px] font-medium ${isGlobalDarkMode ? 'text-sidebar-foreground/60' : 'text-gray-500'}`}>
                        {formatFileSize(backupStorage.used)} of {formatFileSize(backupStorage.total)}
                      </p>
                      {loadingStorage && (
                        <div className="w-2.5 h-2.5 border-2 border-nav-active border-t-transparent rounded-full animate-spin" />
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )
      }
    </div>
  );
};

